// packages/web/src/process/StepScrubber.tsx
//
// Reusable step-scrubber HUD widget for process visualizations.
// Extracted from App.tsx so it can be mounted in both the board HUD
// and the standalone ProcessMode HUD.

import type { WalkResult } from "@archlang/engine";
import { findEnclosingRange } from "./stepRanges.js";
import { displayNumberingFor } from "./displayNumbers.js";
import type { PathGate } from "./pathGates.js";
import { Button, Tooltip, useContextMenu, type ContextMenuItem } from "@archlang/ui";

export interface StepScrubberProps {
  walkResult: WalkResult;
  stepIndex: number | null;
  setStepIndex: (i: number | null) => void;
  autoPlay: boolean;
  setAutoPlay: (b: boolean) => void;
  /** Optional close-process action — renders an X on the left. */
  onClose?: () => void;
  /** Phone treatment: glass full-width transport bar with finger-height
   *  controls (the host docks it bottom-centre, thumb territory). */
  mobile?: boolean;
  /** Locate the CURRENT step on the board (frame its two endpoint nodes).
   *  Renders the n/m counter as a button when provided and a step is active. */
  onLocateStep?: () => void;
  /**
   * Path gating (playback's `gateAdvance`): called before a FORWARD move;
   * returning true means the move needs a path decision (the host surfaces
   * `pendingChoice`) — the scrubber then stays put.
   */
  onGateAdvance?: (toIndex: number) => boolean;
  /** The decision the gate is waiting on — renders the chooser row. */
  pendingChoice?: PathGate | null;
  onChoosePath?: (label: string) => void;
  onDismissChoice?: () => void;
}

export function StepScrubber({
  walkResult,
  stepIndex,
  setStepIndex,
  autoPlay,
  setAutoPlay,
  onClose,
  mobile = false,
  onLocateStep,
  onGateAdvance,
  pendingChoice = null,
  onChoosePath,
  onDismissChoice,
}: StepScrubberProps) {
  // walkResult.steps[].stepIndex is a stable GLOBAL identity (canonical,
  // all-branches — internal only). The scrubber navigates by POSITION within
  // the selected walk and DISPLAYS the sequential run number (dense 1..K,
  // no gaps across branch selections).
  // The scrubber walks the FORWARD happy path only — saga compensation (unwind)
  // steps are the failure/back-flow, reached by clicking a back node, never by
  // stepping forward through the main flow.
  const steps = walkResult.steps.filter((s) => !s.modifiers.compensation);
  const numbering = displayNumberingFor(walkResult);
  const total = numbering.total;
  const currentPos = stepIndex === null
    ? null
    : steps.findIndex((s) => s.stepIndex === stepIndex);
  const positionValid = currentPos !== null && currentPos >= 0;
  // Sequential run number of the active step.
  const displayNumber = positionValid
    ? numbering.byIndex.get(steps[currentPos]!.stepIndex) ?? null
    : null;

  // Range-aware display: if active is inside a collapsed or parallel group,
  // show "a–b" (sequential numbers) instead of a single one.
  const range = findEnclosingRange(walkResult, stepIndex);
  const rangeLabel = range
    ? `${numbering.byIndex.get(range.startIndex) ?? "·"}–${numbering.byIndex.get(range.endIndex) ?? "·"}`
    : null;
  const labelStep = stepIndex === null || displayNumber === null
    ? rangeLabel ?? "—"
    : rangeLabel ?? `${displayNumber}`;
  const labelExtra = range?.label ? ` · ${range.label}` : "";

  const menu = useContextMenu();
  // The step counter is a data element (the active process step): jump to it on
  // the board (reusing onLocateStep) and copy its label. Empty ⇒ no menu.
  const stepMenuItems = (): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];
    if (onLocateStep && positionValid) {
      items.push({ label: "Jump to step", onClick: onLocateStep });
    }
    if (positionValid) {
      const label = `${labelStep}/${total}${labelExtra}`;
      items.push({
        label: "Copy step label",
        separator: items.length > 0,
        onClick: () => void navigator.clipboard?.writeText(label),
      });
    }
    return items;
  };

  function moveTo(target: number, forward: boolean) {
    if (forward && onGateAdvance?.(target)) return; // path decision pending
    setStepIndex(target);
  }

  function step(delta: -1 | 1) {
    setAutoPlay(false);
    if (stepIndex === null) {
      if (steps[0]) moveTo(steps[0].stepIndex, delta > 0);
      return;
    }
    if (range) {
      // Move to the step BEFORE / AFTER the enclosing group (by position).
      const afterEnd = steps.findIndex((s) => s.stepIndex > range.endIndex);
      const beforeStart = [...steps].reverse().find((s) => s.stepIndex < range.startIndex);
      if (delta > 0 && afterEnd >= 0) moveTo(steps[afterEnd]!.stepIndex, true);
      else if (delta < 0 && beforeStart) setStepIndex(beforeStart.stepIndex);
      return;
    }
    if (!positionValid) {
      if (steps[0]) moveTo(steps[0].stepIndex, delta > 0);
      return;
    }
    const nextPos = Math.max(0, Math.min(steps.length - 1, currentPos + delta));
    moveTo(steps[nextPos]!.stepIndex, delta > 0);
  }

  // Shared markup, two skins: the desktop HUD pill, and the phone
  // transport bar (glass, finger-height buttons, spread across the width).
  // Kit `Button ghost sm` supplies the paint, focus ring and disabled state; the
  // mobile skin only overrides the box so the transport keeps finger-height
  // targets (the kit's fixed `h-7` is a desktop-HUD size).
  const btn = mobile ? "h-auto px-3.5 py-2.5 rounded-xl active:bg-white/10" : "";
  const divider = <span className={"w-px bg-slate-700 " + (mobile ? "h-6" : "h-4")} />;
  // The gate's chooser: the advance stopped at an unresolved exclusive frame —
  // pick an arm (records the branch selection, then continues) or dismiss.
  // Rendered as a popover floating BELOW the transport bar (absolute, so it
  // doesn't shift the bar).
  const chooser = pendingChoice && onChoosePath && (
    <div
      data-path-chooser
      className={
        "rounded-lg bg-slate-900/90 backdrop-blur-md border border-sky-700/60 px-2 py-1.5 shadow-lg flex items-center gap-1.5 text-slate-200 " +
        (mobile ? "text-sm w-full max-w-md" : "text-xs")
      }
    >
      <span className="text-sky-300">{pendingChoice.kind === "try" ? "outcome?" : "which path?"}</span>
      {pendingChoice.labels.map((l) => (
        <Button
          key={l}
          variant="ghost"
          size="sm"
          onClick={() => onChoosePath(l)}
          className="border border-slate-600 hover:border-sky-400 hover:bg-sky-500/20"
        >
          {l || "—"}
          {l === pendingChoice.defaultLabel && <span className="text-slate-500 ml-1">(default)</span>}
        </Button>
      ))}
      {onDismissChoice && (
        <Tooltip content="Stay on the current step">
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismissChoice}
            aria-label="Stay on the current step"
          >
            ×
          </Button>
        </Tooltip>
      )}
    </div>
  );
  return (
    <div className={"relative " + (mobile ? "w-full max-w-md flex flex-col items-stretch" : "flex flex-col items-end")}>
    <div
      className={
        mobile
          ? "rounded-2xl bg-slate-900/(--tint-heavy) backdrop-blur-xl border border-white/10 px-1.5 py-1 shadow-lg shadow-black/30 flex w-full max-w-md items-center justify-between gap-0.5 text-sm text-slate-200"
          : "rounded-lg bg-slate-900/(--tint-heavy) backdrop-blur-md border border-slate-700 px-2 py-1.5 shadow-lg flex items-center gap-1 text-xs text-slate-200"
      }
    >
      <Tooltip content="Show all steps">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setStepIndex(null)}
          disabled={stepIndex === null}
          className={btn}
        >
          all
        </Button>
      </Tooltip>
      {divider}
      <Tooltip content="Previous step">
        <Button variant="ghost" size="sm" aria-label="Previous step" onClick={() => step(-1)} className={btn}>◀</Button>
      </Tooltip>
      {onLocateStep && positionValid ? (
        <Tooltip content="Frame this step's nodes on the board">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLocateStep}
            onContextMenu={(e) => menu.open(e, stepMenuItems())}
            className={
              "px-2 font-mono tabular-nums underline decoration-dotted decoration-slate-500 underline-offset-4 " +
              (mobile ? "h-auto min-w-[64px] flex-1 py-2 active:bg-white/10" : "min-w-[70px]")
            }
          >
            {labelStep}/{total}{labelExtra}
          </Button>
        </Tooltip>
      ) : (
        <span
          onContextMenu={(e) => menu.open(e, stepMenuItems())}
          className={"px-2 font-mono tabular-nums text-center " + (mobile ? "min-w-[64px] flex-1" : "min-w-[70px]")}
        >
          {labelStep}/{total}{labelExtra}
        </span>
      )}
      <Tooltip content="Next step">
        <Button variant="ghost" size="sm" aria-label="Next step" onClick={() => step(1)} className={btn}>▶</Button>
      </Tooltip>
      {divider}
      <Tooltip content={autoPlay ? "Pause" : "Play"}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (stepIndex === null && steps[0]) {
              // Starting playback is itself a forward move — gate it too.
              if (onGateAdvance?.(steps[0].stepIndex)) return;
              setStepIndex(steps[0].stepIndex);
            }
            setAutoPlay(!autoPlay);
          }}
          aria-label={autoPlay ? "Pause" : "Play"}
          aria-pressed={autoPlay}
          className={
            btn +
            // Playing is a sustained toggled state, not a variant — keep the
            // accent wash and let ghost supply the idle paint.
            (autoPlay ? " bg-sky-500/30 text-sky-100 hover:bg-sky-500/30 hover:text-sky-100" : "")
          }
        >
          {autoPlay ? "‖" : "▶▶"}
        </Button>
      </Tooltip>
      {onClose && (
        <>
          {divider}
          <Tooltip content="Close process">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close process"
              className={
                (mobile
                  ? "h-auto px-3.5 py-2.5 rounded-xl active:bg-rose-500/20 "
                  : "hover:bg-rose-500/20 hover:text-rose-200 ") +
                "leading-none text-base"
              }
            >
              ×
            </Button>
          </Tooltip>
        </>
      )}
    </div>
    {chooser && (
      <div className={"absolute top-full z-hud mt-1.5 " + (mobile ? "inset-x-0 flex justify-center" : "right-0")}>
        {chooser}
      </div>
    )}
    {menu.node}
    </div>
  );
}

